import React, { useState, useEffect, useRef, useCallback } from 'react';
import './NotificationDialog.css';
import { 
    markNotificationAsRead, 
    setNotificationEngagementFlag, 
    fetchAppliedJobNotifications,
    fetchJobOpeningNotifications
} from '../services/apiService';
import { getCandidateId } from '../services/authService';
// Navigation will use window.location to avoid Router dependency

// Helper function to format salary
const formatSalary = (salary) => {
    if (!salary || salary === 'Not specified') return 'Not specified';
    
    const numSalary = typeof salary === 'string' ? parseFloat(salary) : salary;
    if (isNaN(numSalary)) return 'Not specified';
    
    // Format number with commas and add "per month"
    const formatted = new Intl.NumberFormat('en-IN').format(numSalary);
    return `₹${formatted} per month`;
};

const NotificationDialog = ({ isOpen, onClose, onAnyRead }) => {
    const [activeTab, setActiveTab] = useState('applied-jobs');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [appliedNotifs, setAppliedNotifs] = useState([]);
    const [newJobNotifs, setNewJobNotifs] = useState([]);
    // Removed unused state variables since JobDetailsModal is now handled at app level
    const dialogRef = useRef(null);
    // Note: using window.location for navigation to avoid Router context errors

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target)) {
                if (!event.target.closest('.notification-wrapper')) {
                    onClose();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Helper: relative time formatter (similar to V2 getTimeDifference)
    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        const now = new Date();
        const diffMs = now - date;
        const minutes = Math.floor(diffMs / 60000);
        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes} min ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        const days = Math.floor(hours / 24);
        if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
        const weeks = Math.floor(days / 7);
        if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
        const years = Math.floor(days / 365);
        return `${years} year${years > 1 ? 's' : ''} ago`;
    };

    // Map API item to UI-friendly object (matching Flutter JobNotificationModel)
    const mapNotification = useCallback((n) => {
        const isRead = n?.readStatus === 'Read' || n?.isRead === true;
        return {
            id: n?.id || n?.notificationId || `${n?.notificationType || 'notif'}-${n?.sentOn || Date.now()}`,
            icon: n?.icon || (n?.notificationType === 'AppliedJobUpdate' ? 'fa-clipboard-check' : 'fa-briefcase'),
            title: n?.title || n?.heading || n?.subject || 'Notification',
            description: n?.body || n?.message || n?.description || '',
            time: formatRelativeTime(n?.sentOn || n?.createdOn || n?.createdAt || n?.timestamp),
            unread: !isRead,
            type: n?.notificationType || n?.type || null,
            jobId: n?.budgetId || n?.jobId || n?.jobOpeningId || n?.applicationId || null,
            applicationId: n?.applicationId || null,
            budgetName: n?.budgetName || null,
            cityName: n?.cityName || null,
            salary: n?.salary || null,
            pincode: n?.pincode || null,
            jobDetails: n?.jobDetails || null,
            positionStatus: n?.positionStatus || null,
            raw: n,
        };
    }, []);

    // Fetch notifications whenever dialog opens (fresh fetch every time)
    useEffect(() => {
        let cancelled = false;

        if (!isOpen) {
            return;
        }

        const loadNotifications = async () => {
            setLoading(true);
            setError('');
            try {
                // Use candidateId from auth storage/context; do not call candidate details API
                const candidateId = (typeof getCandidateId === 'function') ? getCandidateId() : null;

                if (!candidateId) {
                    if (!cancelled) setError('Unable to load notifications: candidate not found');
                    return;
                }

                // Engage flag (fire-and-forget) with required candidateId
                setNotificationEngagementFlag(candidateId).catch(() => {});

                // Fetch notifications using new API functions that match Flutter implementation
                const [appliedResponse, jobOpeningResponse] = await Promise.all([
                    fetchAppliedJobNotifications(candidateId),
                    fetchJobOpeningNotifications(candidateId)
                ]);

                if (cancelled) return;

                // Process applied job notifications
                if (appliedResponse?.success) {
                    const applied = (appliedResponse.data || []).map(mapNotification);
                    setAppliedNotifs(applied);
                } else {
                    console.error('Failed to fetch applied job notifications:', appliedResponse?.error);
                    setAppliedNotifs([]);
                }

                // Process job opening notifications
                if (jobOpeningResponse?.success) {
                    const newJobs = (jobOpeningResponse.data || []).map(mapNotification);
                    setNewJobNotifs(newJobs);
                } else {
                    console.error('Failed to fetch job opening notifications:', jobOpeningResponse?.error);
                    setNewJobNotifs([]);
                }

            } catch (err) {
                console.error('Error loading notifications:', err);
                if (!cancelled) setError('Failed to load notifications');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadNotifications();

        return () => { cancelled = true; };
    }, [isOpen, mapNotification]);

    // Optimistically mark as read in UI
    const markAsReadLocally = (id) => {
        setAppliedNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
        setNewJobNotifs((prev) => prev.map((n) => (n.id === id ? { ...n, unread: false } : n)));
    };

    const handleItemClick = async (item) => {
        const notifId = item?.raw?.id || item?.raw?.notificationId || item?.id;
        const budgetId = item?.raw?.budgetId || item?.jobId || item?.budgetId || 
                        item?.raw?.jobId || item?.raw?.positionId || item?.raw?.id || null;
        const applicationId = item?.raw?.applicationId || item?.applicationId || null;

        // 1) Optimistic UI update
        if (notifId) {
            markAsReadLocally(notifId);
        }

        // 2) Mark as read server-side
        try {
            if (notifId) {
                const res = await markNotificationAsRead(notifId);
                if (res?.success && typeof onAnyRead === 'function') onAnyRead();
            }
        } catch (_) {
            // silent
        }

        // 3) Close notification dialog immediately
        onClose();

        // 4) Determine navigation based on notification type
        const notificationType = item?.type || item?.raw?.notificationType;
        
        if (notificationType === 'AppliedJobUpdate' && (budgetId || applicationId)) {
            // For applied job notifications, redirect to My Applications page with specific job highlighted
            const positionId = budgetId || applicationId;
            console.log('[NotificationDialog] Redirecting to Applications with positionId:', positionId);
            
            // Store the highlight ID in sessionStorage to reduce delay
            sessionStorage.setItem('pendingHighlight', positionId);
            
            // Navigate to Applications page with position ID as query parameter
            window.location.href = `/applications?highlight=${positionId}`;
        } else {
            // For new job notifications or other types, show job details modal
            // Create comprehensive job object from notification data
            const budgetCityId = item?.raw?.budgetCityId || item?.budgetCityId || null;
            const jobFromNotification = {
                id: budgetId || notifId || 'notification-' + Date.now(),
                budgetId: budgetId,
                jobId: budgetId,
                positionId: budgetCityId, // Only use budgetCityId as positionId for notifications, never budgetId
                jobName: item?.raw?.budgetName || item?.budgetName || item?.title || item?.raw?.title || item?.raw?.subject || 'Job Details',
                companyName: item?.raw?.companyName || item?.companyName || 'Acer', // Default to Acer based on example
                location: item?.raw?.cityName || item?.cityName || item?.location || 'Location',
                salary: formatSalary(item?.raw?.salary || item?.salary),
                jobType: item?.raw?.jobType || item?.jobType || 'Full-time',
                experience: item?.raw?.experience || item?.experience || 'Not specified',
                skills: item?.raw?.skills || item?.skills || '',
                description: item?.raw?.jobDetails || item?.raw?.body || item?.body || item?.description || 'Job description not available',
                requirements: item?.raw?.requirements || item?.requirements || 'Requirements not specified',
                responsibilities: item?.raw?.responsibilities || item?.responsibilities || 'Responsibilities not specified',
                applicationStatus: item?.raw?.positionStatus || item?.positionStatus || 'Not Applied',
                applicationId: applicationId,
                sentOn: item?.raw?.sentOn || item?.sentOn || new Date().toISOString(),
                notificationType: notificationType,
                // Additional fields from notification response
                readStatus: item?.raw?.readStatus || item?.readStatus || 'Unread',
                budgetCityId: item?.raw?.budgetCityId || item?.budgetCityId || null,
                pinCode: item?.raw?.pinCode || item?.pinCode || null,
                candidateId: item?.raw?.candidateId || item?.candidateId || null,
                // Parse skills into array if it's a comma-separated string
                skillsArray: (item?.raw?.skills || item?.skills || '').split(',').map(skill => skill.trim()).filter(skill => skill.length > 0),
                originalNotification: item
            };
            
            // Dispatch custom event to show job details modal at app level
            const jobDetailsEvent = new CustomEvent('showJobDetailsModal', {
                detail: { job: jobFromNotification }
            });
            window.dispatchEvent(jobDetailsEvent);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div className="notification-dropdown show" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
            <div className="notification-header">
                <h3>Notifications</h3>
                <div className="notification-tabs">
                    <button className={`tab-btn ${activeTab === 'applied-jobs' ? 'active' : ''}`} onClick={() => setActiveTab('applied-jobs')}>Applied Jobs</button>
                    <button className={`tab-btn ${activeTab === 'new-jobs' ? 'active' : ''}`} onClick={() => setActiveTab('new-jobs')}>New Jobs</button>
                </div>
            </div>
            <div className="notification-content">
                {loading && (
                    <div className="notification-list active">
                        {[...Array(4)].map((_, idx) => (
                            <div key={idx} className="notification-item skeleton">
                                <div className="notification-icon"><div className="pulse" /></div>
                                <div className="notification-details">
                                    <div className="line w-60 pulse" />
                                    <div className="line w-90 pulse" />
                                    <div className="line w-30 pulse" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {!loading && error && (
                    <div className="notification-list active">
                        <div className="notification-item">
                            <div className="notification-details">
                                <p style={{ color: '#dc2626' }}>{error}</p>
                            </div>
                        </div>
                    </div>
                )}
                {!loading && !error && (
                    <>
                        <div id="applied-jobs-notifications" className={`notification-list ${activeTab === 'applied-jobs' ? 'active' : ''}`}>
                            {appliedNotifs.length === 0 ? (
                                <div className="notification-item">
                                    <div className="notification-details">
                                        <p>No updates on your applications yet.</p>
                                    </div>
                                </div>
                            ) : (
                                appliedNotifs.map(item => (
                                    <div key={item.id} className={`notification-item ${item.unread ? 'unread' : ''}`} onClick={() => handleItemClick(item)}>
                                        <div className="notification-details">
                                            <h4>{item.title}</h4>
                                            <p>{item.description}</p>
                                            <span className="notification-time">{item.time}</span>
                                        </div>
                                        <div className="notification-arrow">
                                            <i className="fas fa-chevron-right"></i>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div id="new-jobs-notifications" className={`notification-list ${activeTab === 'new-jobs' ? 'active' : ''}`}>
                            {newJobNotifs.length === 0 ? (
                                <div className="notification-item">
                                    <div className="notification-details">
                                        <p>No new job alerts right now.</p>
                                    </div>
                                </div>
                            ) : (
                                newJobNotifs.map(item => (
                                    <div key={item.id} className={`notification-item ${item.unread ? 'unread' : ''}`} onClick={() => handleItemClick(item)}>
                                        <div className="notification-details">
                                            <h4>{item.title}</h4>
                                            <p>{item.description}</p>
                                            <span className="notification-time">{item.time}</span>
                                        </div>
                                        <div className="notification-arrow">
                                            <i className="fas fa-chevron-right"></i>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}

            </div>


        </div>
    );
};

export default NotificationDialog;
